import React, { useState, useCallback, useEffect } from 'react';
import { View, Platform, TextInput, ScrollView, Pressable, useWindowDimensions, Alert } from 'react-native';
import { MotiView } from 'moti';
import { MaterialIcons, MaterialCommunityIcons } from '@expo/vector-icons';
import { YStack, XStack, SizableText, Button, H2, Card } from 'tamagui';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { Text } from '@constants/Themed';
import actionList from './utils/actionList';
import WorkflowCanvas from './WorkflowCanvas';
import ExecutionHistory from './ExecutionHistory';
import FlowHistory from './FlowHistory';
import {
  getTriggerNodeTypes,
  getActionNodeTypes,
  getLogicNodeTypes,
  getDataNodeTypes,
  getErrorNodeTypes,
  NODE_CATEGORIES,
  getNodeType
} from './utils/workflowNodeTypes';

interface Condition {
  id: string;
  field: string;
  operator: string;
  value: string;
  action: string;
}

interface ActionEditorSlidePanelProps {
  visible: boolean;
  onClose: () => void;
  selectedActionType: 'Create' | 'Read' | 'Update' | 'Delete';
  setSelectedActionType: (type: 'Create' | 'Read' | 'Update' | 'Delete') => void;
  activeTab: 'type' | 'config' | 'conditions' | 'workflow' | 'workflowNodes' | 'history' | 'agentic';
  setActiveTab: (tab: 'type' | 'config' | 'conditions' | 'workflow' | 'workflowNodes' | 'history' | 'agentic') => void;
  selectedActionKey: string;
  setSelectedActionKey: (key: string) => void;
  formData: { name: string; description: string };
  setFormData: React.Dispatch<React.SetStateAction<{ name: string; description: string }>>;
  conditions: Condition[];
  setConditions: React.Dispatch<React.SetStateAction<Condition[]>>;
  formRef: React.RefObject<any>;
  actions: any[];
  schemaData: any;
  onSave: () => void;
  getActionTypeInfo: (actionType: 'Create' | 'Read' | 'Update' | 'Delete') => {
    icon: string;
    color: string;
    title: string;
    description: string;
  };
  editorColors: {
    background: string;
    surface: string;
    border: string;
    text: string;
    accent: string;
    secondaryText: string;
    cardBg: string;
  };
  availableFields: Array<{ label: string; value: string; type: string }>;
  availableOperators: Array<{ label: string; value: string; types: string[] }>;
  availableConditionActions: Array<{ label: string; value: string }>;
  handleAddCondition: () => void;
  handleUpdateCondition: (conditionId: string, field: string, value: any) => void;
  handleRemoveCondition: (conditionId: string) => void;
  // Workflow props
  workflowId?: string;
  workflowNodes?: Array<{ id: string; type: string; name: string; position: { x: number; y: number }; config?: any }>;
  workflowConnections?: Array<{ source: { nodeId: string; outputIndex: number }; target: { nodeId: string; inputIndex: number }; type?: string }>;
  onWorkflowNodeMove?: (nodeId: string, position: { x: number; y: number }) => void;
  onWorkflowNodeAdd?: (node: { type: string; name: string; category: string; position: { x: number; y: number }; config?: any }) => void;
  onWorkflowSave?: () => void;
  // Editor mode props
  editorMode?: 'action' | 'workflow';
  onEditorModeChange?: (mode: 'action' | 'workflow') => void;
  currentWorkflowId?: string | null;
  isLoadingWorkflow?: boolean;
  // Current action being edited
  editingAction?: any;
}

const ActionEditorSlidePanel: React.FC<ActionEditorSlidePanelProps> = ({
  visible,
  onClose,
  selectedActionType,
  setSelectedActionType,
  activeTab,
  setActiveTab,
  selectedActionKey,
  setSelectedActionKey,
  formData,
  setFormData,
  conditions,
  setConditions,
  formRef,
  actions,
  schemaData,
  onSave,
  getActionTypeInfo,
  editorColors,
  availableFields,
  availableOperators,
  availableConditionActions,
  handleAddCondition,
  handleUpdateCondition,
  handleRemoveCondition,
  workflowId,
  workflowNodes = [],
  workflowConnections = [],
  onWorkflowNodeMove,
  onWorkflowNodeAdd,
  onWorkflowSave,
  editorMode: externalEditorMode,
  onEditorModeChange,
  currentWorkflowId,
  isLoadingWorkflow = false,
  editingAction,
}) => {
  const theme = themeSelector();
  const { width, height } = useWindowDimensions();
  const statusBarHeight = Platform.OS === 'ios' ? 44 : 0;
  const modalHeight = Platform.OS === 'ios' ? height - statusBarHeight : height;
  const actionTypeInfo = getActionTypeInfo(selectedActionType);
  const [workflowZoom, setWorkflowZoom] = useState(1);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [showExecutionHistory, setShowExecutionHistory] = useState(false);
  const [selectedNodeCategory, setSelectedNodeCategory] = useState<string | null>(null);
  const [selectedNodeType, setSelectedNodeType] = useState<string | null>(null);
  const [agenticFlowEnabled, setAgenticFlowEnabled] = useState(false);
  const [localAgenticFlowConfig, setLocalAgenticFlowConfig] = useState<any>(null);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    agents: true,
    flowSettings: false,
    performance: false,
    errorHandling: false,
    monitoring: false,
    security: false,
    costManagement: false,
    decisionPoints: false,
    triggers: false,
    contextSources: false
  });
  const [newContextSource, setNewContextSource] = useState('');
  const [newDecisionPoint, setNewDecisionPoint] = useState({ condition: '', actions: [] as string[] });

  // Internal editor mode state (if not controlled externally)
  const [internalEditorMode, setInternalEditorMode] = useState<'action' | 'workflow'>('action');
  const editorMode = externalEditorMode !== undefined ? externalEditorMode : internalEditorMode;

  // Handle mode switch
  const handleModeSwitch = useCallback(() => {
    const newMode = editorMode === 'action' ? 'workflow' : 'action';
    if (onEditorModeChange) {
      onEditorModeChange(newMode);
    } else {
      setInternalEditorMode(newMode);
    }
    // Switch to appropriate tab
    if (newMode === 'workflow') {
      setActiveTab('workflow');
    } else {
      setActiveTab('type');
    }
  }, [editorMode, onEditorModeChange]);

  // Get current action being edited
  const getCurrentAction = useCallback(() => {
    // First try editingAction prop (most reliable)
    if (editingAction) {
      return editingAction;
    }
    // Fallback to finding by formRef actionId
    if (formRef.current?.actionId) {
      return actions.find(a => a.id === formRef.current?.actionId || a._id === formRef.current?.actionId);
    }
    return null;
  }, [actions, formRef, editingAction]);

  // Initialize agentic flow state when action changes
  useEffect(() => {
    const currentAction = getCurrentAction();
    if (currentAction) {
      const enabled = currentAction.agenticFlow?.enabled || false;
      setAgenticFlowEnabled(enabled);
      if (currentAction.agenticFlow) {
        setLocalAgenticFlowConfig(currentAction.agenticFlow);
      } else {
        setLocalAgenticFlowConfig(null);
      }
    } else {
      setAgenticFlowEnabled(false);
      setLocalAgenticFlowConfig(null);
    }
  }, [getCurrentAction, editingAction, activeTab]);

  // Helper function to update agentic flow config
  const updateAgenticFlowConfig = useCallback((path: string[], value: any) => {
    const action = getCurrentAction();
    const updatedConfig = { ...localAgenticFlowConfig };
    let current = updatedConfig;
    
    // Navigate to the nested path
    for (let i = 0; i < path.length - 1; i++) {
      if (!current[path[i]]) {
        current[path[i]] = {};
      }
      current = current[path[i]];
    }
    
    // Set the value
    current[path[path.length - 1]] = value;
    
    setLocalAgenticFlowConfig(updatedConfig);
    
    // Update action if it exists
    if (action) {
      if (!action.agenticFlow) action.agenticFlow = {};
      let actionCurrent = action.agenticFlow;
      for (let i = 0; i < path.length - 1; i++) {
        if (!actionCurrent[path[i]]) {
          actionCurrent[path[i]] = {};
        }
        actionCurrent = actionCurrent[path[i]];
      }
      actionCurrent[path[path.length - 1]] = value;
    }
  }, [localAgenticFlowConfig, getCurrentAction]);

  // Toggle section expansion
  const toggleSection = useCallback((section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  }, []);

  // Add context source
  const handleAddContextSource = useCallback(() => {
    if (newContextSource.trim()) {
      const currentSources = localAgenticFlowConfig?.agents?.decisionMaker?.contextSources || [];
      const updatedSources = [...currentSources, newContextSource.trim()];
      updateAgenticFlowConfig(['agents', 'decisionMaker', 'contextSources'], updatedSources);
      setNewContextSource('');
    }
  }, [newContextSource, localAgenticFlowConfig, updateAgenticFlowConfig]);

  // Remove context source
  const handleRemoveContextSource = useCallback((index: number) => {
    const currentSources = localAgenticFlowConfig?.agents?.decisionMaker?.contextSources || [];
    const updatedSources = currentSources.filter((_: any, i: number) => i !== index);
    updateAgenticFlowConfig(['agents', 'decisionMaker', 'contextSources'], updatedSources);
  }, [localAgenticFlowConfig, updateAgenticFlowConfig]);

  // Add decision point
  const handleAddDecisionPoint = useCallback(() => {
    if (newDecisionPoint.condition.trim()) {
      const currentPoints = localAgenticFlowConfig?.decisionPoints || [];
      const newPoint = {
        id: `dp_${Date.now()}`,
        condition: newDecisionPoint.condition.trim(),
        actions: newDecisionPoint.actions
      };
      updateAgenticFlowConfig(['decisionPoints'], [...currentPoints, newPoint]);
      setNewDecisionPoint({ condition: '', actions: [] });
    }
  }, [newDecisionPoint, localAgenticFlowConfig, updateAgenticFlowConfig]);

  // Remove decision point
  const handleRemoveDecisionPoint = useCallback((id: string) => {
    const currentPoints = localAgenticFlowConfig?.decisionPoints || [];
    const updatedPoints = currentPoints.filter((dp: any) => dp.id !== id);
    updateAgenticFlowConfig(['decisionPoints'], updatedPoints);
  }, [localAgenticFlowConfig, updateAgenticFlowConfig]);

  return (
    <MotiView
      from={{
        translateX: width,
        opacity: 0
      }}
      animate={{
        translateX: visible ? 0 : width,
        opacity: visible ? 1 : 0
      }}
      exit={{
        translateX: width,
        opacity: 0
      }}
      transition={{
        type: 'timing',
        duration: 300,
      }}
      style={{
        position: 'absolute',
        top: statusBarHeight * 1.4,
        right: 0,
        width: Platform.OS === 'web' ? width * 0.4 : width * 0.85,
        height: modalHeight,
        borderStartStartRadius: 20,
        borderBottomStartRadius: 20,
        overflow: 'hidden',
        maxHeight: "90%",
        backgroundColor: editorColors.background,
        ...Platform.select({
          web: {
            boxShadow: '0 0 20px rgba(0, 0, 0, 0.3)',
          },
          default: {
            shadowColor: '#000',
            shadowOffset: { width: -2, height: 0 },
            shadowOpacity: 0.3,
            shadowRadius: 10,
            elevation: 10,
          }
        })
      }}
    >
      <YStack flex={1} space="$0">
        {/* Header Section */}
        <YStack
          padding="$5"
          paddingBottom="$4"
          borderBottomWidth={1}
          borderBottomColor={editorColors.border}
          backgroundColor={editorColors.surface}
        >
          <XStack alignItems="center" justifyContent="space-between" marginBottom="$2">
            <XStack alignItems="center" gap={12} flex={1}>
              {actionTypeInfo && (
                <View style={{
                  width: 40,
                  height: 40,
                  borderRadius: 20,
                  backgroundColor: actionTypeInfo.color + '20',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <MaterialIcons
                    name={actionTypeInfo.icon as any}
                    size={24}
                    color={actionTypeInfo.color}
                  />
                </View>
              )}
              <YStack flex={1}>
                <H2
                  fontSize="$6"
                  fontWeight="700"
                  color={editorColors.text}
                >
                  {actionTypeInfo?.title || 'Action Editor'}
                </H2>
                {conditions.length > 0 && editorMode === 'action' && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <MaterialCommunityIcons name="filter-check" size={12} color={editorColors.accent} />
                    <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>
                      {conditions.length} condition{conditions.length !== 1 ? 's' : ''} active
                    </Text>
                  </View>
                )}
                {editorMode === 'workflow' && currentWorkflowId && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginTop: 4 }}>
                    <MaterialIcons name="account-tree" size={12} color={editorColors.accent} />
                    <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>
                      Editing workflow
                    </Text>
                  </View>
                )}
              </YStack>
            </XStack>
            <XStack gap={8} alignItems="center">
              {/* Mode Switch Button - Only show if workflowId exists */}
              {currentWorkflowId && (
                <Pressable
                  onPress={handleModeSwitch}
                  style={{
                    padding: 8,
                    borderRadius: 8,
                    backgroundColor: editorMode === 'workflow' ? editorColors.accent : editorColors.cardBg,
                    borderWidth: 1,
                    borderColor: editorMode === 'workflow' ? editorColors.accent : editorColors.border,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 4
                  }}
                >
                  <MaterialIcons
                    name={editorMode === 'workflow' ? 'settings' : 'account-tree'}
                    size={16}
                    color={editorMode === 'workflow' ? 'white' : editorColors.text}
                  />
                  <Text style={{
                    fontSize: 11,
                    fontWeight: '600',
                    color: editorMode === 'workflow' ? 'white' : editorColors.text
                  }}>
                    {editorMode === 'workflow' ? 'Action' : 'Workflow'}
                  </Text>
                </Pressable>
              )}
              <Pressable
                onPress={onClose}
                style={{
                  padding: 8,
                  borderRadius: 8,
                  backgroundColor: editorColors.border,
                }}
              >
                <MaterialIcons
                  name="close"
                  size={20}
                  color={editorColors.text}
                />
              </Pressable>
            </XStack>
          </XStack>
          <SizableText
            color={editorColors.secondaryText}
            size="$4"
            lineHeight="$5"
          >
            {actionTypeInfo?.description || 'Configure your action settings'}
          </SizableText>
        </YStack>

        {/* Tabs */}
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={{
            borderBottomWidth: 1,
            maxHeight: 60,
            borderBottomColor: editorColors.border,
            backgroundColor: editorColors.surface
          }}
          contentContainerStyle={{ paddingHorizontal: 16 }}
        >
          <XStack gap={8} maxHeight={60} paddingVertical={12}>
            {(() => {
              // Show different tabs based on editor mode
              if (editorMode === 'workflow') {
                return [
                  { id: 'workflow', label: 'Workflow', icon: 'account-tree' },
                  { id: 'workflowNodes', label: 'Workflow Nodes', icon: 'device-hub' },
                  { id: 'history', label: 'History', icon: 'history' },
                ];
              } else {
                return [
                  { id: 'type', label: 'Action Type', icon: 'category' },
                  { id: 'config', label: 'Configuration', icon: 'settings' },
                  { id: 'conditions', label: 'Conditions', icon: 'rule' },
                  { id: 'agentic', label: 'Agentic Flow', icon: 'psychology' },
                ];
              }
            })().map(tab => (
              <Pressable
                key={tab.id}
                onPress={() => setActiveTab(tab.id as any)}
                style={{
                  paddingHorizontal: 16,
                  paddingVertical: 8,
                  borderRadius: 8,
                  backgroundColor: activeTab === tab.id ? editorColors.accent : 'transparent',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 6
                }}
              >
                <MaterialIcons
                  name={tab.icon as any}
                  size={16}
                  color={activeTab === tab.id ? 'white' : editorColors.text}
                />
                <Text style={{
                  fontSize: 13,
                  minHeight: 30,
                  fontWeight: '600',
                  color: activeTab === tab.id ? 'white' : editorColors.text
                }}>
                  {tab.label}
                </Text>
              </Pressable>
            ))}
          </XStack>
        </ScrollView>

        {/* Content Section */}
        <YStack
          flex={1}
          padding="$5"
          backgroundColor={editorColors.background}
        >
          <ScrollView style={{ flex: 1 }} showsVerticalScrollIndicator={false}>
            {activeTab === 'workflow' ? (
              /* Workflow Builder */
              <YStack flex={1} gap={12}>
                <Text style={{ fontSize: 16, fontWeight: '600', color: editorColors.text, marginBottom: 8 }}>
                  Visual Workflow Builder
                </Text>
                <View style={{ flex: 1, minHeight: 400 }}>
                  <WorkflowCanvas
                    nodes={workflowNodes}
                    connections={workflowConnections}
                    onNodeMove={onWorkflowNodeMove}
                    onNodeSelect={setSelectedNodeId}
                    selectedNodeId={selectedNodeId}
                    zoom={workflowZoom}
                    onZoomChange={setWorkflowZoom}
                    onNodeAdd={(type: string, position: { x: number; y: number }) => {
                      // Convert to node format expected by onWorkflowNodeAdd
                      if (onWorkflowNodeAdd) {
                        const nodeTypeDef = getNodeType(type);
                        onWorkflowNodeAdd({
                          type,
                          name: nodeTypeDef?.name || type,
                          category: nodeTypeDef?.category || 'action',
                          position
                        });
                      }
                    }}
                  />
                </View>
                <XStack gap={8} marginTop={12}>
                  <Button
                    backgroundColor={editorColors.accent}
                    borderRadius={8}
                    paddingHorizontal="$4"
                    paddingVertical="$3"
                    onPress={() => {
                      const newNode = {
                        type: 'manual',
                        name: 'Manual Trigger',
                        category: 'trigger',
                        position: { x: Math.random() * 300, y: Math.random() * 200 }
                      };
                      onWorkflowNodeAdd?.(newNode);
                    }}
                  >
                    <SizableText color="white" fontWeight="600" size="$3">
                      Add Node
                    </SizableText>
                  </Button>
                  <Button
                    backgroundColor={editorColors.cardBg}
                    borderColor={editorColors.border}
                    borderWidth={1}
                    borderRadius={8}
                    paddingHorizontal="$4"
                    paddingVertical="$3"
                    onPress={() => {
                      setActiveTab('history');
                      setShowExecutionHistory(true);
                    }}
                  >
                    <SizableText color={editorColors.text} fontWeight="600" size="$3">
                      View History
                    </SizableText>
                  </Button>
                </XStack>
              </YStack>
            ) : editorMode === 'workflow' && activeTab === 'workflowNodes' ? (
              /* Workflow Nodes Tab */
              <YStack gap={16}>
                <Text style={{ fontSize: 18, fontWeight: '700', color: editorColors.text, marginBottom: 4 }}>
                  Add Workflow Node
                </Text>
                <Text style={{ fontSize: 13, color: editorColors.secondaryText, marginBottom: 16 }}>
                  Select a node category and type to add to your workflow
                </Text>

                {/* Node Category Selection */}
                <YStack gap={12}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                    Category
                  </Text>
                  <XStack flexWrap="wrap" gap={8}>
                    {[
                      { id: NODE_CATEGORIES.TRIGGER, label: 'Triggers', icon: 'power', color: '#ff6d5a' },
                      { id: NODE_CATEGORIES.ACTION, label: 'Actions', icon: 'flash-on', color: '#6bcf7f' },
                      { id: NODE_CATEGORIES.LOGIC, label: 'Logic', icon: 'device-hub', color: '#ffd93d' },
                      { id: NODE_CATEGORIES.DATA, label: 'Data', icon: 'data-usage', color: '#ff9500' },
                      { id: NODE_CATEGORIES.ERROR, label: 'Error', icon: 'error', color: '#ff4444' },
                    ].map(category => (
                      <Pressable
                        key={category.id}
                        onPress={() => {
                          setSelectedNodeCategory(category.id);
                          setSelectedNodeType(null); // Reset node type when category changes
                        }}
                        style={{
                          flexDirection: 'row',
                          alignItems: 'center',
                          padding: 12,
                          borderRadius: 10,
                          backgroundColor: selectedNodeCategory === category.id
                            ? category.color + '20'
                            : editorColors.cardBg,
                          borderWidth: 2,
                          borderColor: selectedNodeCategory === category.id
                            ? category.color
                            : editorColors.border,
                          gap: 8,
                          minWidth: 120
                        }}
                      >
                        <MaterialIcons
                          name={category.icon as any}
                          size={20}
                          color={selectedNodeCategory === category.id ? category.color : editorColors.text}
                        />
                        <Text style={{
                          fontSize: 13,
                          fontWeight: '600',
                          color: selectedNodeCategory === category.id ? category.color : editorColors.text
                        }}>
                          {category.label}
                        </Text>
                      </Pressable>
                    ))}
                  </XStack>
                </YStack>

                {/* Node Type Selection */}
                {selectedNodeCategory && (
                  <YStack gap={12}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                      Node Type
                    </Text>
                    <YStack gap={8}>
                      {(() => {
                        let nodeTypes: any[] = [];
                        switch (selectedNodeCategory) {
                          case NODE_CATEGORIES.TRIGGER:
                            nodeTypes = getTriggerNodeTypes();
                            break;
                          case NODE_CATEGORIES.ACTION:
                            nodeTypes = getActionNodeTypes();
                            break;
                          case NODE_CATEGORIES.LOGIC:
                            nodeTypes = getLogicNodeTypes();
                            break;
                          case NODE_CATEGORIES.DATA:
                            nodeTypes = getDataNodeTypes();
                            break;
                          case NODE_CATEGORIES.ERROR:
                            nodeTypes = getErrorNodeTypes();
                            break;
                        }
                        return nodeTypes.map(nodeType => (
                          <Pressable
                            key={nodeType.type}
                            onPress={() => setSelectedNodeType(nodeType.type)}
                            style={{
                              flexDirection: 'row',
                              alignItems: 'center',
                              padding: 14,
                              borderRadius: 10,
                              backgroundColor: selectedNodeType === nodeType.type
                                ? nodeType.color + '20'
                                : editorColors.cardBg,
                              borderWidth: 2,
                              borderColor: selectedNodeType === nodeType.type
                                ? nodeType.color
                                : editorColors.border,
                              gap: 12
                            }}
                          >
                            <View style={{
                              width: 40,
                              height: 40,
                              borderRadius: 20,
                              backgroundColor: nodeType.color + '30',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}>
                              <MaterialIcons
                                name={nodeType.icon}
                                size={22}
                                color={nodeType.color}
                              />
                            </View>
                            <YStack flex={1}>
                              <Text style={{
                                fontSize: 15,
                                fontWeight: '700',
                                color: selectedNodeType === nodeType.type ? nodeType.color : editorColors.text,
                                marginBottom: 2
                              }}>
                                {nodeType.name}
                              </Text>
                              <Text style={{
                                fontSize: 12,
                                color: editorColors.secondaryText
                              }}>
                                {nodeType.description}
                              </Text>
                            </YStack>
                            {selectedNodeType === nodeType.type && (
                              <MaterialIcons name="check-circle" size={24} color={nodeType.color} />
                            )}
                          </Pressable>
                        ));
                      })()}
                    </YStack>
                  </YStack>
                )}

                {/* Node Configuration Preview */}
                {selectedNodeType && (
                  <YStack gap={12} padding={16} backgroundColor={editorColors.cardBg} borderRadius={12}>
                    <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                      Node Configuration
                    </Text>
                    <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>
                      Node will be added to the workflow with default configuration. You can configure it after adding.
                    </Text>
                  </YStack>
                )}
              </YStack>
            ) : editorMode === 'workflow' && activeTab === 'history' ? (
              /* Execution History */
              <YStack gap={12} flex={1}>
                {schemaData?._id || schemaData?.id ? (
                  <FlowHistory
                    dataId={schemaData._id || schemaData.id}
                    workflowId={workflowId || currentWorkflowId || undefined}
                    visible={true}
                    onClose={() => setShowExecutionHistory(false)}
                  />
                ) : (
                  <View style={{ padding: 20, alignItems: 'center' }}>
                    <Text style={{ color: editorColors.secondaryText }}>
                      No data schema available. Cannot load execution history.
                    </Text>
                  </View>
                )}
              </YStack>
            ) : editorMode === 'action' && activeTab === 'type' ? (
              /* Action Type Selection */
              <YStack gap={12}>
                <Text style={{ fontSize: 16, fontWeight: '600', color: editorColors.text, marginBottom: 8 }}>
                  Select Action Type
                </Text>
                {(['Create', 'Read', 'Update', 'Delete'] as const).map(type => {
                  const typeInfo = getActionTypeInfo(type);
                  const isSelected = selectedActionType === type;
                  return (
                    <Pressable
                      key={type}
                      onPress={() => setSelectedActionType(type)}
                      style={{
                        flexDirection: 'row',
                        alignItems: 'center',
                        padding: 16,
                        borderRadius: 12,
                        backgroundColor: isSelected ? typeInfo.color + '20' : editorColors.cardBg,
                        borderWidth: 2,
                        borderColor: isSelected ? typeInfo.color : editorColors.border,
                        gap: 12
                      }}
                    >
                      <View style={{
                        width: 48,
                        height: 48,
                        borderRadius: 24,
                        backgroundColor: typeInfo.color + '30',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}>
                        <MaterialIcons
                          name={typeInfo.icon as any}
                          size={28}
                          color={typeInfo.color}
                        />
                      </View>
                      <YStack flex={1}>
                        <Text style={{ fontSize: 16, fontWeight: '700', color: typeInfo.color, marginBottom: 2 }}>
                          {typeInfo.title}
                        </Text>
                        <Text style={{ fontSize: 13, color: editorColors.secondaryText }}>
                          {typeInfo.description}
                        </Text>
                      </YStack>
                      {isSelected && (
                        <MaterialIcons name="check-circle" size={24} color={typeInfo.color} />
                      )}
                    </Pressable>
                  );
                })}
              </YStack>
                  ) : editorMode === 'action' && activeTab === 'agentic' ? (
              /* Agentic Flow Tab */
              <YStack gap={20}>
                {/* Info Banner */}
                <View style={{
                  backgroundColor: theme ? '#fef3c7' : '#78350f',
                  borderRadius: 12,
                  padding: 16,
                  borderWidth: 1,
                  borderColor: theme ? '#fcd34d' : '#fbbf24'
                }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <MaterialIcons name="psychology" size={20} color={theme ? '#d97706' : '#fbbf24'} />
                    <Text style={{ fontSize: 14, fontWeight: '600', color: theme ? '#d97706' : '#fbbf24' }}>
                      Agentic Flow Configuration
                    </Text>
                  </View>
                  <Text style={{ fontSize: 12, color: theme ? '#92400e' : '#fcd34d', lineHeight: 18 }}>
                    Enable AI-powered automation with dynamic decision-making, real-time adaptation, and multi-agent orchestration.
                  </Text>
                </View>

                {/* Enable Agentic Mode Toggle */}
                <YStack gap={12}>
                  <XStack alignItems="center" justifyContent="space-between">
                    <YStack flex={1}>
                      <SizableText fontSize="$4" fontWeight="600" color={editorColors.text} marginBottom="$1">
                        Enable Agentic Mode
                      </SizableText>
                      <SizableText fontSize="$2" color={editorColors.secondaryText}>
                        Transform this action into an AI-powered agentic flow
                      </SizableText>
                    </YStack>
                    <Pressable
                      onPress={() => {
                        console.log('🔄 Toggling agentic flow mode', { agenticFlowEnabled, editingAction, formRef: formRef.current });
                        
                        // Toggle agentic mode
                        const currentAction = getCurrentAction();
                        console.log('📋 Current action:', currentAction);
                        
                        const newEnabled = !agenticFlowEnabled;
                        setAgenticFlowEnabled(newEnabled);
                        
                        // Create or update agentic flow config
                        const existingConfig = currentAction?.agenticFlow || localAgenticFlowConfig;
                        const newConfig = {
                          enabled: newEnabled,
                          agents: {
                            decisionMaker: { 
                              role: existingConfig?.agents?.decisionMaker?.role || 'Decision Maker', 
                              goal: existingConfig?.agents?.decisionMaker?.goal || '', 
                              contextSources: existingConfig?.agents?.decisionMaker?.contextSources || [],
                              model: existingConfig?.agents?.decisionMaker?.model || 'gpt-4',
                              temperature: existingConfig?.agents?.decisionMaker?.temperature ?? 0.7,
                              maxTokens: existingConfig?.agents?.decisionMaker?.maxTokens ?? 2000,
                              systemPrompt: existingConfig?.agents?.decisionMaker?.systemPrompt || ''
                            },
                            actionExecutor: { 
                              role: existingConfig?.agents?.actionExecutor?.role || 'Action Executor', 
                              goal: existingConfig?.agents?.actionExecutor?.goal || '', 
                              availableActions: existingConfig?.agents?.actionExecutor?.availableActions || [],
                              model: existingConfig?.agents?.actionExecutor?.model || 'gpt-4',
                              temperature: existingConfig?.agents?.actionExecutor?.temperature ?? 0.5,
                              maxTokens: existingConfig?.agents?.actionExecutor?.maxTokens ?? 1500
                            },
                            contextAnalyzer: { 
                              role: existingConfig?.agents?.contextAnalyzer?.role || 'Context Analyzer', 
                              goal: existingConfig?.agents?.contextAnalyzer?.goal || '', 
                              monitoringFields: existingConfig?.agents?.contextAnalyzer?.monitoringFields || [],
                              model: existingConfig?.agents?.contextAnalyzer?.model || 'gpt-3.5-turbo',
                              temperature: existingConfig?.agents?.contextAnalyzer?.temperature ?? 0.3,
                              maxTokens: existingConfig?.agents?.contextAnalyzer?.maxTokens ?? 1000
                            }
                          },
                          decisionPoints: existingConfig?.decisionPoints || [],
                          adaptiveRouting: existingConfig?.adaptiveRouting || false,
                          maxIterations: existingConfig?.maxIterations || 10,
                          triggerEvents: existingConfig?.triggerEvents || ['create', 'update'],
                          // Performance settings
                          timeout: existingConfig?.timeout ?? 30000,
                          maxRetries: existingConfig?.maxRetries ?? 3,
                          retryBackoff: existingConfig?.retryBackoff || 'exponential',
                          rateLimit: existingConfig?.rateLimit ?? 100,
                          concurrentLimit: existingConfig?.concurrentLimit ?? 5,
                          // Error handling
                          errorHandling: existingConfig?.errorHandling || {
                            strategy: 'retry',
                            fallbackAction: null,
                            notifyOnError: true
                          },
                          // Monitoring
                          monitoring: existingConfig?.monitoring || {
                            enabled: true,
                            logLevel: 'info',
                            metricsCollection: true,
                            debugMode: false
                          },
                          // Security
                          security: existingConfig?.security || {
                            apiKeyRequired: false,
                            accessControl: 'inherit',
                            dataPrivacy: 'standard'
                          },
                          // Cost management
                          costManagement: existingConfig?.costManagement || {
                            enabled: true,
                            maxCostPerExecution: null,
                            tokenUsageTracking: true,
                            budgetAlertThreshold: 80
                          }
                        };
                        
                        console.log('✅ New agentic flow config:', newConfig);
                        setLocalAgenticFlowConfig(newConfig);
                        
                        // Update the action object if it exists
                        if (currentAction) {
                          currentAction.agenticFlow = newConfig;
                          console.log('✅ Updated action.agenticFlow:', currentAction.agenticFlow);
                        } else {
                          // If no action exists yet (new action), store in formRef for later
                          if (formRef.current) {
                            formRef.current.agenticFlow = newConfig;
                            console.log('✅ Stored agenticFlow in formRef:', formRef.current.agenticFlow);
                          }
                        }
                      }}
                      style={{
                        width: 50,
                        height: 30,
                        borderRadius: 15,
                        backgroundColor: agenticFlowEnabled ? editorColors.accent : editorColors.border,
                        justifyContent: 'center',
                        paddingHorizontal: 4
                      }}
                    >
                      <View
                        style={{
                          width: 22,
                          height: 22,
                          borderRadius: 11,
                          backgroundColor: 'white',
                          alignSelf: agenticFlowEnabled ? 'flex-end' : 'flex-start'
                        }}
                      />
                    </Pressable>
                  </XStack>

                  {agenticFlowEnabled && (
                    <YStack gap={16} marginTop="$3">
                      {/* Agent Configuration */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <SizableText fontSize="$5" fontWeight="600" color={editorColors.text} marginBottom="$3">
                          Agent Configuration
                        </SizableText>
                        
                        {/* Decision Maker Agent */}
                        <YStack gap="$2" marginBottom="$4">
                          <SizableText fontSize="$3" fontWeight="600" color={editorColors.text}>
                            Decision Maker Agent
                          </SizableText>
                          <TextInput
                            placeholder="Role (e.g., Decision Maker)"
                            value={localAgenticFlowConfig?.agents?.decisionMaker?.role || getCurrentAction()?.agenticFlow?.agents?.decisionMaker?.role || 'Decision Maker'}
                            onChangeText={(text) => {
                              const action = getCurrentAction();
                              const updatedConfig = {
                                ...localAgenticFlowConfig,
                                agents: {
                                  ...localAgenticFlowConfig?.agents,
                                  decisionMaker: {
                                    ...localAgenticFlowConfig?.agents?.decisionMaker,
                                    role: text
                                  }
                                }
                              };
                              setLocalAgenticFlowConfig(updatedConfig);
                              if (action) {
                                if (!action.agenticFlow) action.agenticFlow = {};
                                if (!action.agenticFlow.agents) action.agenticFlow.agents = {};
                                if (!action.agenticFlow.agents.decisionMaker) action.agenticFlow.agents.decisionMaker = {};
                                action.agenticFlow.agents.decisionMaker.role = text;
                              }
                            }}
                            style={{
                              backgroundColor: editorColors.background,
                              borderRadius: 8,
                              padding: 12,
                              fontSize: 14,
                              color: editorColors.text,
                              borderWidth: 1,
                              borderColor: editorColors.border
                            }}
                          />
                          <TextInput
                            placeholder="Goal: Describe what decisions this agent should make"
                            value={localAgenticFlowConfig?.agents?.decisionMaker?.goal || getCurrentAction()?.agenticFlow?.agents?.decisionMaker?.goal || ''}
                            onChangeText={(text) => {
                              const action = getCurrentAction();
                              const updatedConfig = {
                                ...localAgenticFlowConfig,
                                agents: {
                                  ...localAgenticFlowConfig?.agents,
                                  decisionMaker: {
                                    ...localAgenticFlowConfig?.agents?.decisionMaker,
                                    goal: text
                                  }
                                }
                              };
                              setLocalAgenticFlowConfig(updatedConfig);
                              if (action) {
                                if (!action.agenticFlow) action.agenticFlow = {};
                                if (!action.agenticFlow.agents) action.agenticFlow.agents = {};
                                if (!action.agenticFlow.agents.decisionMaker) action.agenticFlow.agents.decisionMaker = {};
                                action.agenticFlow.agents.decisionMaker.goal = text;
                              }
                            }}
                            multiline
                            numberOfLines={3}
                            style={{
                              backgroundColor: editorColors.background,
                              borderRadius: 8,
                              padding: 12,
                              fontSize: 14,
                              color: editorColors.text,
                              borderWidth: 1,
                              borderColor: editorColors.border,
                              minHeight: 80
                            }}
                          />
                        </YStack>

                        {/* Action Executor Agent */}
                        <YStack gap="$2" marginBottom="$4">
                          <SizableText fontSize="$3" fontWeight="600" color={editorColors.text}>
                            Action Executor Agent
                          </SizableText>
                          <TextInput
                            placeholder="Role (e.g., Action Executor)"
                            value={localAgenticFlowConfig?.agents?.actionExecutor?.role || getCurrentAction()?.agenticFlow?.agents?.actionExecutor?.role || 'Action Executor'}
                            onChangeText={(text) => {
                              const action = getCurrentAction();
                              const updatedConfig = {
                                ...localAgenticFlowConfig,
                                agents: {
                                  ...localAgenticFlowConfig?.agents,
                                  actionExecutor: {
                                    ...localAgenticFlowConfig?.agents?.actionExecutor,
                                    role: text
                                  }
                                }
                              };
                              setLocalAgenticFlowConfig(updatedConfig);
                              if (action) {
                                if (!action.agenticFlow) action.agenticFlow = {};
                                if (!action.agenticFlow.agents) action.agenticFlow.agents = {};
                                if (!action.agenticFlow.agents.actionExecutor) action.agenticFlow.agents.actionExecutor = {};
                                action.agenticFlow.agents.actionExecutor.role = text;
                              }
                            }}
                            style={{
                              backgroundColor: editorColors.background,
                              borderRadius: 8,
                              padding: 12,
                              fontSize: 14,
                              color: editorColors.text,
                              borderWidth: 1,
                              borderColor: editorColors.border
                            }}
                          />
                          <TextInput
                            placeholder="Goal: Describe how to execute actions"
                            value={localAgenticFlowConfig?.agents?.actionExecutor?.goal || getCurrentAction()?.agenticFlow?.agents?.actionExecutor?.goal || ''}
                            onChangeText={(text) => {
                              const action = getCurrentAction();
                              const updatedConfig = {
                                ...localAgenticFlowConfig,
                                agents: {
                                  ...localAgenticFlowConfig?.agents,
                                  actionExecutor: {
                                    ...localAgenticFlowConfig?.agents?.actionExecutor,
                                    goal: text
                                  }
                                }
                              };
                              setLocalAgenticFlowConfig(updatedConfig);
                              if (action) {
                                if (!action.agenticFlow) action.agenticFlow = {};
                                if (!action.agenticFlow.agents) action.agenticFlow.agents = {};
                                if (!action.agenticFlow.agents.actionExecutor) action.agenticFlow.agents.actionExecutor = {};
                                action.agenticFlow.agents.actionExecutor.goal = text;
                              }
                            }}
                            multiline
                            numberOfLines={3}
                            style={{
                              backgroundColor: editorColors.background,
                              borderRadius: 8,
                              padding: 12,
                              fontSize: 14,
                              color: editorColors.text,
                              borderWidth: 1,
                              borderColor: editorColors.border,
                              minHeight: 80
                            }}
                          />
                        </YStack>

                        {/* Context Analyzer Agent */}
                        <YStack gap="$2">
                          <SizableText fontSize="$3" fontWeight="600" color={editorColors.text}>
                            Context Analyzer Agent
                          </SizableText>
                          <TextInput
                            placeholder="Role (e.g., Context Analyzer)"
                            value={localAgenticFlowConfig?.agents?.contextAnalyzer?.role || getCurrentAction()?.agenticFlow?.agents?.contextAnalyzer?.role || 'Context Analyzer'}
                            onChangeText={(text) => {
                              const action = getCurrentAction();
                              const updatedConfig = {
                                ...localAgenticFlowConfig,
                                agents: {
                                  ...localAgenticFlowConfig?.agents,
                                  contextAnalyzer: {
                                    ...localAgenticFlowConfig?.agents?.contextAnalyzer,
                                    role: text
                                  }
                                }
                              };
                              setLocalAgenticFlowConfig(updatedConfig);
                              if (action) {
                                if (!action.agenticFlow) action.agenticFlow = {};
                                if (!action.agenticFlow.agents) action.agenticFlow.agents = {};
                                if (!action.agenticFlow.agents.contextAnalyzer) action.agenticFlow.agents.contextAnalyzer = {};
                                action.agenticFlow.agents.contextAnalyzer.role = text;
                              }
                            }}
                            style={{
                              backgroundColor: editorColors.background,
                              borderRadius: 8,
                              padding: 12,
                              fontSize: 14,
                              color: editorColors.text,
                              borderWidth: 1,
                              borderColor: editorColors.border
                            }}
                          />
                          <TextInput
                            placeholder="Goal: Describe what context to monitor"
                            value={localAgenticFlowConfig?.agents?.contextAnalyzer?.goal || getCurrentAction()?.agenticFlow?.agents?.contextAnalyzer?.goal || ''}
                            onChangeText={(text) => {
                              const action = getCurrentAction();
                              const updatedConfig = {
                                ...localAgenticFlowConfig,
                                agents: {
                                  ...localAgenticFlowConfig?.agents,
                                  contextAnalyzer: {
                                    ...localAgenticFlowConfig?.agents?.contextAnalyzer,
                                    goal: text
                                  }
                                }
                              };
                              setLocalAgenticFlowConfig(updatedConfig);
                              if (action) {
                                if (!action.agenticFlow) action.agenticFlow = {};
                                if (!action.agenticFlow.agents) action.agenticFlow.agents = {};
                                if (!action.agenticFlow.agents.contextAnalyzer) action.agenticFlow.agents.contextAnalyzer = {};
                                action.agenticFlow.agents.contextAnalyzer.goal = text;
                              }
                            }}
                            multiline
                            numberOfLines={3}
                            style={{
                              backgroundColor: editorColors.background,
                              borderRadius: 8,
                              padding: 12,
                              fontSize: 14,
                              color: editorColors.text,
                              borderWidth: 1,
                              borderColor: editorColors.border,
                              minHeight: 80
                            }}
                          />
                        </YStack>
                      </Card>

                      {/* Flow Settings */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <SizableText fontSize="$5" fontWeight="600" color={editorColors.text} marginBottom="$3">
                          Flow Settings
                        </SizableText>
                        
                        <YStack gap="$3">
                          <XStack alignItems="center" justifyContent="space-between">
                            <YStack flex={1}>
                              <SizableText fontSize="$3" fontWeight="600" color={editorColors.text}>
                                Adaptive Routing
                              </SizableText>
                              <SizableText fontSize="$2" color={editorColors.secondaryText}>
                                Allow dynamic path changes during execution
                              </SizableText>
                            </YStack>
                            <Pressable
                              onPress={() => {
                                const action = getCurrentAction();
                                const currentValue = localAgenticFlowConfig?.adaptiveRouting ?? action?.agenticFlow?.adaptiveRouting ?? false;
                                const newValue = !currentValue;
                                const updatedConfig = {
                                  ...localAgenticFlowConfig,
                                  adaptiveRouting: newValue
                                };
                                setLocalAgenticFlowConfig(updatedConfig);
                                if (action) {
                                  if (!action.agenticFlow) action.agenticFlow = {};
                                  action.agenticFlow.adaptiveRouting = newValue;
                                }
                              }}
                              style={{
                                width: 50,
                                height: 30,
                                borderRadius: 15,
                                backgroundColor: (localAgenticFlowConfig?.adaptiveRouting ?? getCurrentAction()?.agenticFlow?.adaptiveRouting) ? editorColors.accent : editorColors.border,
                                justifyContent: 'center',
                                paddingHorizontal: 4
                              }}
                            >
                              <View
                                style={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: 11,
                                  backgroundColor: 'white',
                                  alignSelf: (localAgenticFlowConfig?.adaptiveRouting ?? getCurrentAction()?.agenticFlow?.adaptiveRouting) ? 'flex-end' : 'flex-start'
                                }}
                              />
                            </Pressable>
                          </XStack>

                          <YStack gap="$2">
                            <SizableText fontSize="$3" fontWeight="600" color={editorColors.text}>
                              Max Iterations
                            </SizableText>
                            <TextInput
                              placeholder="10"
                              value={String(localAgenticFlowConfig?.maxIterations ?? getCurrentAction()?.agenticFlow?.maxIterations ?? 10)}
                              onChangeText={(text) => {
                                const action = getCurrentAction();
                                const newValue = parseInt(text) || 10;
                                const updatedConfig = {
                                  ...localAgenticFlowConfig,
                                  maxIterations: newValue
                                };
                                setLocalAgenticFlowConfig(updatedConfig);
                                if (action) {
                                  if (!action.agenticFlow) action.agenticFlow = {};
                                  action.agenticFlow.maxIterations = newValue;
                                }
                              }}
                              keyboardType="numeric"
                              style={{
                                backgroundColor: editorColors.background,
                                borderRadius: 8,
                                padding: 12,
                                fontSize: 14,
                                color: editorColors.text,
                                borderWidth: 1,
                                borderColor: editorColors.border
                              }}
                            />
                          </YStack>
                        </YStack>
                      </Card>

                      {/* Advanced Agent Configuration - Expandable */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <Pressable onPress={() => toggleSection('agents')}>
                          <XStack alignItems="center" justifyContent="space-between" marginBottom={expandedSections.agents ? "$3" : 0}>
                            <XStack alignItems="center" gap={8}>
                              <MaterialIcons name={expandedSections.agents ? 'expand-less' : 'expand-more'} size={20} color={editorColors.text} />
                              <SizableText fontSize="$5" fontWeight="600" color={editorColors.text}>
                                Advanced Agent Settings
                              </SizableText>
                            </XStack>
                            <MaterialIcons name="tune" size={20} color={editorColors.accent} />
                          </XStack>
                        </Pressable>
                        
                        {expandedSections.agents && (
                          <YStack gap="$4">
                            {/* Decision Maker Advanced Settings */}
                            <YStack gap="$2" padding="$3" backgroundColor={editorColors.background} borderRadius={8}>
                              <SizableText fontSize="$3" fontWeight="600" color={editorColors.text}>
                                Decision Maker - Model Configuration
                              </SizableText>
                              <YStack gap="$2">
                                <YStack gap="$1">
                                  <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Model</Text>
                                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                                    <XStack gap={6}>
                                      {['gpt-4', 'gpt-4-turbo', 'gpt-3.5-turbo', 'claude-3-opus', 'claude-3-sonnet'].map(model => (
                                        <Pressable
                                          key={model}
                                          onPress={() => updateAgenticFlowConfig(['agents', 'decisionMaker', 'model'], model)}
                                          style={{
                                            paddingHorizontal: 12,
                                            paddingVertical: 6,
                                            borderRadius: 6,
                                            backgroundColor: (localAgenticFlowConfig?.agents?.decisionMaker?.model || 'gpt-4') === model
                                              ? editorColors.accent : editorColors.cardBg,
                                            borderWidth: 1,
                                            borderColor: (localAgenticFlowConfig?.agents?.decisionMaker?.model || 'gpt-4') === model
                                              ? editorColors.accent : editorColors.border
                                          }}
                                        >
                                          <Text style={{
                                            fontSize: 11,
                                            color: (localAgenticFlowConfig?.agents?.decisionMaker?.model || 'gpt-4') === model
                                              ? 'white' : editorColors.text
                                          }}>{model}</Text>
                                        </Pressable>
                                      ))}
                                    </XStack>
                                  </ScrollView>
                                </YStack>
                                <YStack gap="$1">
                                  <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Temperature</Text>
                                  <XStack gap={8} alignItems="center">
                                    <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                                      <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>0.0</Text>
                                      <View style={{ flex: 1, height: 4, backgroundColor: editorColors.border, borderRadius: 2 }}>
                                        <View style={{
                                          width: `${Math.min(100, ((localAgenticFlowConfig?.agents?.decisionMaker?.temperature ?? 0.7) / 2) * 100)}%`,
                                          height: 4,
                                          backgroundColor: editorColors.accent,
                                          borderRadius: 2
                                        }} />
                                      </View>
                                      <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>2.0</Text>
                                    </View>
                                    <TextInput
                                      value={String(localAgenticFlowConfig?.agents?.decisionMaker?.temperature ?? 0.7)}
                                      onChangeText={(text) => {
                                        const val = parseFloat(text);
                                        if (!isNaN(val) && val >= 0 && val <= 2) {
                                          updateAgenticFlowConfig(['agents', 'decisionMaker', 'temperature'], val);
                                        }
                                      }}
                                      keyboardType="decimal-pad"
                                      style={{
                                        width: 60,
                                        backgroundColor: editorColors.cardBg,
                                        borderRadius: 6,
                                        padding: 6,
                                        fontSize: 12,
                                        color: editorColors.text,
                                        borderWidth: 1,
                                        borderColor: editorColors.border,
                                        textAlign: 'center'
                                      }}
                                    />
                                  </XStack>
                                </YStack>
                                <YStack gap="$1">
                                  <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Max Tokens</Text>
                                  <TextInput
                                    value={String(localAgenticFlowConfig?.agents?.decisionMaker?.maxTokens ?? 2000)}
                                    onChangeText={(text) => updateAgenticFlowConfig(['agents', 'decisionMaker', 'maxTokens'], parseInt(text) || 2000)}
                                    keyboardType="numeric"
                                    style={{
                                      backgroundColor: editorColors.cardBg,
                                      borderRadius: 6,
                                      padding: 8,
                                      fontSize: 12,
                                      color: editorColors.text,
                                      borderWidth: 1,
                                      borderColor: editorColors.border
                                    }}
                                  />
                                </YStack>
                                <YStack gap="$1">
                                  <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>System Prompt (Optional)</Text>
                                  <TextInput
                                    value={localAgenticFlowConfig?.agents?.decisionMaker?.systemPrompt || ''}
                                    onChangeText={(text) => updateAgenticFlowConfig(['agents', 'decisionMaker', 'systemPrompt'], text)}
                                    multiline
                                    numberOfLines={3}
                                    placeholder="Custom system prompt for decision making..."
                                    placeholderTextColor={editorColors.secondaryText}
                                    style={{
                                      backgroundColor: editorColors.cardBg,
                                      borderRadius: 6,
                                      padding: 8,
                                      fontSize: 12,
                                      color: editorColors.text,
                                      borderWidth: 1,
                                      borderColor: editorColors.border,
                                      minHeight: 60
                                    }}
                                  />
                                </YStack>
                              </YStack>
                            </YStack>
                          </YStack>
                        )}
                      </Card>

                      {/* Performance & Reliability - Expandable */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <Pressable onPress={() => toggleSection('performance')}>
                          <XStack alignItems="center" justifyContent="space-between" marginBottom={expandedSections.performance ? "$3" : 0}>
                            <XStack alignItems="center" gap={8}>
                              <MaterialIcons name={expandedSections.performance ? 'expand-less' : 'expand-more'} size={20} color={editorColors.text} />
                              <SizableText fontSize="$5" fontWeight="600" color={editorColors.text}>
                                Performance & Reliability
                              </SizableText>
                            </XStack>
                            <MaterialIcons name="speed" size={20} color={editorColors.accent} />
                          </XStack>
                        </Pressable>
                        
                        {expandedSections.performance && (
                          <YStack gap="$3">
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Timeout (ms)</Text>
                              <TextInput
                                value={String(localAgenticFlowConfig?.timeout ?? 30000)}
                                onChangeText={(text) => updateAgenticFlowConfig(['timeout'], parseInt(text) || 30000)}
                                keyboardType="numeric"
                                style={{
                                  backgroundColor: editorColors.background,
                                  borderRadius: 6,
                                  padding: 10,
                                  fontSize: 13,
                                  color: editorColors.text,
                                  borderWidth: 1,
                                  borderColor: editorColors.border
                                }}
                              />
                            </YStack>
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Max Retries</Text>
                              <TextInput
                                value={String(localAgenticFlowConfig?.maxRetries ?? 3)}
                                onChangeText={(text) => updateAgenticFlowConfig(['maxRetries'], parseInt(text) || 3)}
                                keyboardType="numeric"
                                style={{
                                  backgroundColor: editorColors.background,
                                  borderRadius: 6,
                                  padding: 10,
                                  fontSize: 13,
                                  color: editorColors.text,
                                  borderWidth: 1,
                                  borderColor: editorColors.border
                                }}
                              />
                            </YStack>
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Retry Backoff Strategy</Text>
                              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                                <XStack gap={6}>
                                  {['exponential', 'linear', 'fixed'].map(strategy => (
                                    <Pressable
                                      key={strategy}
                                      onPress={() => updateAgenticFlowConfig(['retryBackoff'], strategy)}
                                      style={{
                                        paddingHorizontal: 12,
                                        paddingVertical: 6,
                                        borderRadius: 6,
                                        backgroundColor: (localAgenticFlowConfig?.retryBackoff || 'exponential') === strategy
                                          ? editorColors.accent : editorColors.cardBg,
                                        borderWidth: 1,
                                        borderColor: (localAgenticFlowConfig?.retryBackoff || 'exponential') === strategy
                                          ? editorColors.accent : editorColors.border
                                      }}
                                    >
                                      <Text style={{
                                        fontSize: 11,
                                        color: (localAgenticFlowConfig?.retryBackoff || 'exponential') === strategy
                                          ? 'white' : editorColors.text
                                      }}>{strategy}</Text>
                                    </Pressable>
                                  ))}
                                </XStack>
                              </ScrollView>
                            </YStack>
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Rate Limit (requests/minute)</Text>
                              <TextInput
                                value={String(localAgenticFlowConfig?.rateLimit ?? 100)}
                                onChangeText={(text) => updateAgenticFlowConfig(['rateLimit'], parseInt(text) || 100)}
                                keyboardType="numeric"
                                style={{
                                  backgroundColor: editorColors.background,
                                  borderRadius: 6,
                                  padding: 10,
                                  fontSize: 13,
                                  color: editorColors.text,
                                  borderWidth: 1,
                                  borderColor: editorColors.border
                                }}
                              />
                            </YStack>
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Concurrent Execution Limit</Text>
                              <TextInput
                                value={String(localAgenticFlowConfig?.concurrentLimit ?? 5)}
                                onChangeText={(text) => updateAgenticFlowConfig(['concurrentLimit'], parseInt(text) || 5)}
                                keyboardType="numeric"
                                style={{
                                  backgroundColor: editorColors.background,
                                  borderRadius: 6,
                                  padding: 10,
                                  fontSize: 13,
                                  color: editorColors.text,
                                  borderWidth: 1,
                                  borderColor: editorColors.border
                                }}
                              />
                            </YStack>
                          </YStack>
                        )}
                      </Card>

                      {/* Error Handling - Expandable */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <Pressable onPress={() => toggleSection('errorHandling')}>
                          <XStack alignItems="center" justifyContent="space-between" marginBottom={expandedSections.errorHandling ? "$3" : 0}>
                            <XStack alignItems="center" gap={8}>
                              <MaterialIcons name={expandedSections.errorHandling ? 'expand-less' : 'expand-more'} size={20} color={editorColors.text} />
                              <SizableText fontSize="$5" fontWeight="600" color={editorColors.text}>
                                Error Handling
                              </SizableText>
                            </XStack>
                            <MaterialIcons name="error-outline" size={20} color="#ef4444" />
                          </XStack>
                        </Pressable>
                        
                        {expandedSections.errorHandling && (
                          <YStack gap="$3">
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Error Handling Strategy</Text>
                              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                                <XStack gap={6}>
                                  {['retry', 'fallback', 'fail', 'notify'].map(strategy => (
                                    <Pressable
                                      key={strategy}
                                      onPress={() => {
                                        const current = localAgenticFlowConfig?.errorHandling || {};
                                        updateAgenticFlowConfig(['errorHandling'], { ...current, strategy });
                                      }}
                                      style={{
                                        paddingHorizontal: 12,
                                        paddingVertical: 6,
                                        borderRadius: 6,
                                        backgroundColor: (localAgenticFlowConfig?.errorHandling?.strategy || 'retry') === strategy
                                          ? editorColors.accent : editorColors.cardBg,
                                        borderWidth: 1,
                                        borderColor: (localAgenticFlowConfig?.errorHandling?.strategy || 'retry') === strategy
                                          ? editorColors.accent : editorColors.border
                                      }}
                                    >
                                      <Text style={{
                                        fontSize: 11,
                                        color: (localAgenticFlowConfig?.errorHandling?.strategy || 'retry') === strategy
                                          ? 'white' : editorColors.text
                                      }}>{strategy}</Text>
                                    </Pressable>
                                  ))}
                                </XStack>
                              </ScrollView>
                            </YStack>
                            <XStack alignItems="center" justifyContent="space-between">
                              <YStack flex={1}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text }}>
                                  Notify on Error
                                </Text>
                                <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>
                                  Send notifications when errors occur
                                </Text>
                              </YStack>
                              <Pressable
                                onPress={() => {
                                  const current = localAgenticFlowConfig?.errorHandling || {};
                                  updateAgenticFlowConfig(['errorHandling'], { ...current, notifyOnError: !(current.notifyOnError ?? true) });
                                }}
                                style={{
                                  width: 50,
                                  height: 30,
                                  borderRadius: 15,
                                  backgroundColor: (localAgenticFlowConfig?.errorHandling?.notifyOnError ?? true) ? editorColors.accent : editorColors.border,
                                  justifyContent: 'center',
                                  paddingHorizontal: 4
                                }}
                              >
                                <View style={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: 11,
                                  backgroundColor: 'white',
                                  alignSelf: (localAgenticFlowConfig?.errorHandling?.notifyOnError ?? true) ? 'flex-end' : 'flex-start'
                                }} />
                              </Pressable>
                            </XStack>
                          </YStack>
                        )}
                      </Card>

                      {/* Monitoring & Observability - Expandable */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <Pressable onPress={() => toggleSection('monitoring')}>
                          <XStack alignItems="center" justifyContent="space-between" marginBottom={expandedSections.monitoring ? "$3" : 0}>
                            <XStack alignItems="center" gap={8}>
                              <MaterialIcons name={expandedSections.monitoring ? 'expand-less' : 'expand-more'} size={20} color={editorColors.text} />
                              <SizableText fontSize="$5" fontWeight="600" color={editorColors.text}>
                                Monitoring & Observability
                              </SizableText>
                            </XStack>
                            <MaterialIcons name="insights" size={20} color={editorColors.accent} />
                          </XStack>
                        </Pressable>
                        
                        {expandedSections.monitoring && (
                          <YStack gap="$3">
                            <XStack alignItems="center" justifyContent="space-between">
                              <YStack flex={1}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text }}>
                                  Enable Monitoring
                                </Text>
                                <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>
                                  Track execution metrics and performance
                                </Text>
                              </YStack>
                              <Pressable
                                onPress={() => {
                                  const current = localAgenticFlowConfig?.monitoring || {};
                                  updateAgenticFlowConfig(['monitoring'], { ...current, enabled: !(current.enabled ?? true) });
                                }}
                                style={{
                                  width: 50,
                                  height: 30,
                                  borderRadius: 15,
                                  backgroundColor: (localAgenticFlowConfig?.monitoring?.enabled ?? true) ? editorColors.accent : editorColors.border,
                                  justifyContent: 'center',
                                  paddingHorizontal: 4
                                }}
                              >
                                <View style={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: 11,
                                  backgroundColor: 'white',
                                  alignSelf: (localAgenticFlowConfig?.monitoring?.enabled ?? true) ? 'flex-end' : 'flex-start'
                                }} />
                              </Pressable>
                            </XStack>
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Log Level</Text>
                              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                                <XStack gap={6}>
                                  {['debug', 'info', 'warn', 'error'].map(level => (
                                    <Pressable
                                      key={level}
                                      onPress={() => {
                                        const current = localAgenticFlowConfig?.monitoring || {};
                                        updateAgenticFlowConfig(['monitoring'], { ...current, logLevel: level });
                                      }}
                                      style={{
                                        paddingHorizontal: 12,
                                        paddingVertical: 6,
                                        borderRadius: 6,
                                        backgroundColor: (localAgenticFlowConfig?.monitoring?.logLevel || 'info') === level
                                          ? editorColors.accent : editorColors.cardBg,
                                        borderWidth: 1,
                                        borderColor: (localAgenticFlowConfig?.monitoring?.logLevel || 'info') === level
                                          ? editorColors.accent : editorColors.border
                                      }}
                                    >
                                      <Text style={{
                                        fontSize: 11,
                                        color: (localAgenticFlowConfig?.monitoring?.logLevel || 'info') === level
                                          ? 'white' : editorColors.text
                                      }}>{level}</Text>
                                    </Pressable>
                                  ))}
                                </XStack>
                              </ScrollView>
                            </YStack>
                            <XStack alignItems="center" justifyContent="space-between">
                              <YStack flex={1}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text }}>
                                  Metrics Collection
                                </Text>
                                <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>
                                  Collect performance metrics
                                </Text>
                              </YStack>
                              <Pressable
                                onPress={() => {
                                  const current = localAgenticFlowConfig?.monitoring || {};
                                  updateAgenticFlowConfig(['monitoring'], { ...current, metricsCollection: !(current.metricsCollection ?? true) });
                                }}
                                style={{
                                  width: 50,
                                  height: 30,
                                  borderRadius: 15,
                                  backgroundColor: (localAgenticFlowConfig?.monitoring?.metricsCollection ?? true) ? editorColors.accent : editorColors.border,
                                  justifyContent: 'center',
                                  paddingHorizontal: 4
                                }}
                              >
                                <View style={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: 11,
                                  backgroundColor: 'white',
                                  alignSelf: (localAgenticFlowConfig?.monitoring?.metricsCollection ?? true) ? 'flex-end' : 'flex-start'
                                }} />
                              </Pressable>
                            </XStack>
                            <XStack alignItems="center" justifyContent="space-between">
                              <YStack flex={1}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text }}>
                                  Debug Mode
                                </Text>
                                <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>
                                  Enable detailed debugging logs
                                </Text>
                              </YStack>
                              <Pressable
                                onPress={() => {
                                  const current = localAgenticFlowConfig?.monitoring || {};
                                  updateAgenticFlowConfig(['monitoring'], { ...current, debugMode: !(current.debugMode ?? false) });
                                }}
                                style={{
                                  width: 50,
                                  height: 30,
                                  borderRadius: 15,
                                  backgroundColor: (localAgenticFlowConfig?.monitoring?.debugMode ?? false) ? editorColors.accent : editorColors.border,
                                  justifyContent: 'center',
                                  paddingHorizontal: 4
                                }}
                              >
                                <View style={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: 11,
                                  backgroundColor: 'white',
                                  alignSelf: (localAgenticFlowConfig?.monitoring?.debugMode ?? false) ? 'flex-end' : 'flex-start'
                                }} />
                              </Pressable>
                            </XStack>
                          </YStack>
                        )}
                      </Card>

                      {/* Context Sources Management - Expandable */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <Pressable onPress={() => toggleSection('contextSources')}>
                          <XStack alignItems="center" justifyContent="space-between" marginBottom={expandedSections.contextSources ? "$3" : 0}>
                            <XStack alignItems="center" gap={8}>
                              <MaterialIcons name={expandedSections.contextSources ? 'expand-less' : 'expand-more'} size={20} color={editorColors.text} />
                              <SizableText fontSize="$5" fontWeight="600" color={editorColors.text}>
                                Context Sources
                              </SizableText>
                            </XStack>
                            <MaterialIcons name="source" size={20} color={editorColors.accent} />
                          </XStack>
                        </Pressable>
                        
                        {expandedSections.contextSources && (
                          <YStack gap="$3">
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Add Context Source</Text>
                              <XStack gap={8}>
                                <TextInput
                                  value={newContextSource}
                                  onChangeText={setNewContextSource}
                                  placeholder="e.g., user_profile, external_api, database"
                                  placeholderTextColor={editorColors.secondaryText}
                                  style={{
                                    flex: 1,
                                    backgroundColor: editorColors.background,
                                    borderRadius: 6,
                                    padding: 10,
                                    fontSize: 13,
                                    color: editorColors.text,
                                    borderWidth: 1,
                                    borderColor: editorColors.border
                                  }}
                                />
                                <Button
                                  backgroundColor={editorColors.accent}
                                  borderRadius={6}
                                  paddingHorizontal="$3"
                                  paddingVertical="$2"
                                  onPress={handleAddContextSource}
                                >
                                  <MaterialIcons name="add" size={16} color="white" />
                                </Button>
                              </XStack>
                            </YStack>
                            {(localAgenticFlowConfig?.agents?.decisionMaker?.contextSources || []).length > 0 && (
                              <YStack gap="$2">
                                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text }}>
                                  Active Context Sources
                                </Text>
                                {(localAgenticFlowConfig?.agents?.decisionMaker?.contextSources || []).map((source: string, index: number) => (
                                  <XStack key={index} alignItems="center" justifyContent="space-between" padding="$2" backgroundColor={editorColors.background} borderRadius={6}>
                                    <Text style={{ fontSize: 12, color: editorColors.text }}>{source}</Text>
                                    <Pressable onPress={() => handleRemoveContextSource(index)}>
                                      <MaterialIcons name="close" size={18} color="#ef4444" />
                                    </Pressable>
                                  </XStack>
                                ))}
                              </YStack>
                            )}
                          </YStack>
                        )}
                      </Card>

                      {/* Decision Points - Expandable */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <Pressable onPress={() => toggleSection('decisionPoints')}>
                          <XStack alignItems="center" justifyContent="space-between" marginBottom={expandedSections.decisionPoints ? "$3" : 0}>
                            <XStack alignItems="center" gap={8}>
                              <MaterialIcons name={expandedSections.decisionPoints ? 'expand-less' : 'expand-more'} size={20} color={editorColors.text} />
                              <SizableText fontSize="$5" fontWeight="600" color={editorColors.text}>
                                Decision Points
                              </SizableText>
                            </XStack>
                            <MaterialIcons name="account-tree" size={20} color={editorColors.accent} />
                          </XStack>
                        </Pressable>
                        
                        {expandedSections.decisionPoints && (
                          <YStack gap="$3">
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Condition</Text>
                              <TextInput
                                value={newDecisionPoint.condition}
                                onChangeText={(text) => setNewDecisionPoint(prev => ({ ...prev, condition: text }))}
                                placeholder="e.g., user.age > 18 AND user.status === 'active'"
                                placeholderTextColor={editorColors.secondaryText}
                                style={{
                                  backgroundColor: editorColors.background,
                                  borderRadius: 6,
                                  padding: 10,
                                  fontSize: 13,
                                  color: editorColors.text,
                                  borderWidth: 1,
                                  borderColor: editorColors.border
                                }}
                              />
                            </YStack>
                            <Button
                              backgroundColor={editorColors.accent}
                              borderRadius={6}
                              paddingHorizontal="$4"
                              paddingVertical="$3"
                              onPress={handleAddDecisionPoint}
                            >
                              <SizableText color="white" fontWeight="600" size="$3">
                                Add Decision Point
                              </SizableText>
                            </Button>
                            {(localAgenticFlowConfig?.decisionPoints || []).length > 0 && (
                              <YStack gap="$2">
                                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text }}>
                                  Active Decision Points
                                </Text>
                                {(localAgenticFlowConfig?.decisionPoints || []).map((dp: any) => (
                                  <XStack key={dp.id} alignItems="center" justifyContent="space-between" padding="$2" backgroundColor={editorColors.background} borderRadius={6}>
                                    <YStack flex={1}>
                                      <Text style={{ fontSize: 12, color: editorColors.text }}>{dp.condition}</Text>
                                      {dp.actions && dp.actions.length > 0 && (
                                        <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>
                                          Actions: {dp.actions.join(', ')}
                                        </Text>
                                      )}
                                    </YStack>
                                    <Pressable onPress={() => handleRemoveDecisionPoint(dp.id)}>
                                      <MaterialIcons name="delete" size={18} color="#ef4444" />
                                    </Pressable>
                                  </XStack>
                                ))}
                              </YStack>
                            )}
                          </YStack>
                        )}
                      </Card>

                      {/* Trigger Events - Expandable */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <Pressable onPress={() => toggleSection('triggers')}>
                          <XStack alignItems="center" justifyContent="space-between" marginBottom={expandedSections.triggers ? "$3" : 0}>
                            <XStack alignItems="center" gap={8}>
                              <MaterialIcons name={expandedSections.triggers ? 'expand-less' : 'expand-more'} size={20} color={editorColors.text} />
                              <SizableText fontSize="$5" fontWeight="600" color={editorColors.text}>
                                Trigger Events
                              </SizableText>
                            </XStack>
                            <MaterialIcons name="event" size={20} color={editorColors.accent} />
                          </XStack>
                        </Pressable>
                        
                        {expandedSections.triggers && (
                          <YStack gap="$3">
                            <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Select events that trigger this flow</Text>
                            <YStack gap="$2">
                              {['create', 'update', 'delete'].map(event => {
                                const isSelected = (localAgenticFlowConfig?.triggerEvents || ['create', 'update']).includes(event);
                                return (
                                  <Pressable
                                    key={event}
                                    onPress={() => {
                                      const current = localAgenticFlowConfig?.triggerEvents || ['create', 'update'];
                                      const updated = isSelected
                                        ? current.filter((e: string) => e !== event)
                                        : [...current, event];
                                      updateAgenticFlowConfig(['triggerEvents'], updated);
                                    }}
                                    style={{
                                      flexDirection: 'row',
                                      alignItems: 'center',
                                      padding: 12,
                                      borderRadius: 8,
                                      backgroundColor: isSelected ? editorColors.accent + '20' : editorColors.background,
                                      borderWidth: 1,
                                      borderColor: isSelected ? editorColors.accent : editorColors.border
                                    }}
                                  >
                                    <MaterialIcons
                                      name={isSelected ? 'check-box' : 'check-box-outline-blank'}
                                      size={20}
                                      color={isSelected ? editorColors.accent : editorColors.text}
                                    />
                                    <Text style={{
                                      fontSize: 13,
                                      fontWeight: '600',
                                      color: isSelected ? editorColors.accent : editorColors.text,
                                      marginLeft: 8
                                    }}>
                                      {event.charAt(0).toUpperCase() + event.slice(1)}
                                    </Text>
                                  </Pressable>
                                );
                              })}
                            </YStack>
                          </YStack>
                        )}
                      </Card>

                      {/* Cost Management - Expandable */}
                      <Card backgroundColor={editorColors.cardBg} padding="$4">
                        <Pressable onPress={() => toggleSection('costManagement')}>
                          <XStack alignItems="center" justifyContent="space-between" marginBottom={expandedSections.costManagement ? "$3" : 0}>
                            <XStack alignItems="center" gap={8}>
                              <MaterialIcons name={expandedSections.costManagement ? 'expand-less' : 'expand-more'} size={20} color={editorColors.text} />
                              <SizableText fontSize="$5" fontWeight="600" color={editorColors.text}>
                                Cost Management
                              </SizableText>
                            </XStack>
                            <MaterialIcons name="attach-money" size={20} color="#10b981" />
                          </XStack>
                        </Pressable>
                        
                        {expandedSections.costManagement && (
                          <YStack gap="$3">
                            <XStack alignItems="center" justifyContent="space-between">
                              <YStack flex={1}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text }}>
                                  Enable Cost Tracking
                                </Text>
                                <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>
                                  Monitor and control execution costs
                                </Text>
                              </YStack>
                              <Pressable
                                onPress={() => {
                                  const current = localAgenticFlowConfig?.costManagement || {};
                                  updateAgenticFlowConfig(['costManagement'], { ...current, enabled: !(current.enabled ?? true) });
                                }}
                                style={{
                                  width: 50,
                                  height: 30,
                                  borderRadius: 15,
                                  backgroundColor: (localAgenticFlowConfig?.costManagement?.enabled ?? true) ? editorColors.accent : editorColors.border,
                                  justifyContent: 'center',
                                  paddingHorizontal: 4
                                }}
                              >
                                <View style={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: 11,
                                  backgroundColor: 'white',
                                  alignSelf: (localAgenticFlowConfig?.costManagement?.enabled ?? true) ? 'flex-end' : 'flex-start'
                                }} />
                              </Pressable>
                            </XStack>
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Max Cost Per Execution ($)</Text>
                              <TextInput
                                value={localAgenticFlowConfig?.costManagement?.maxCostPerExecution ? String(localAgenticFlowConfig.costManagement.maxCostPerExecution) : ''}
                                onChangeText={(text) => {
                                  const current = localAgenticFlowConfig?.costManagement || {};
                                  updateAgenticFlowConfig(['costManagement'], {
                                    ...current,
                                    maxCostPerExecution: text ? parseFloat(text) : null
                                  });
                                }}
                                keyboardType="decimal-pad"
                                placeholder="Leave empty for no limit"
                                placeholderTextColor={editorColors.secondaryText}
                                style={{
                                  backgroundColor: editorColors.background,
                                  borderRadius: 6,
                                  padding: 10,
                                  fontSize: 13,
                                  color: editorColors.text,
                                  borderWidth: 1,
                                  borderColor: editorColors.border
                                }}
                              />
                            </YStack>
                            <XStack alignItems="center" justifyContent="space-between">
                              <YStack flex={1}>
                                <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text }}>
                                  Token Usage Tracking
                                </Text>
                                <Text style={{ fontSize: 11, color: editorColors.secondaryText }}>
                                  Track token consumption per execution
                                </Text>
                              </YStack>
                              <Pressable
                                onPress={() => {
                                  const current = localAgenticFlowConfig?.costManagement || {};
                                  updateAgenticFlowConfig(['costManagement'], { ...current, tokenUsageTracking: !(current.tokenUsageTracking ?? true) });
                                }}
                                style={{
                                  width: 50,
                                  height: 30,
                                  borderRadius: 15,
                                  backgroundColor: (localAgenticFlowConfig?.costManagement?.tokenUsageTracking ?? true) ? editorColors.accent : editorColors.border,
                                  justifyContent: 'center',
                                  paddingHorizontal: 4
                                }}
                              >
                                <View style={{
                                  width: 22,
                                  height: 22,
                                  borderRadius: 11,
                                  backgroundColor: 'white',
                                  alignSelf: (localAgenticFlowConfig?.costManagement?.tokenUsageTracking ?? true) ? 'flex-end' : 'flex-start'
                                }} />
                              </Pressable>
                            </XStack>
                            <YStack gap="$2">
                              <Text style={{ fontSize: 12, color: editorColors.secondaryText }}>Budget Alert Threshold (%)</Text>
                              <TextInput
                                value={String(localAgenticFlowConfig?.costManagement?.budgetAlertThreshold ?? 80)}
                                onChangeText={(text) => {
                                  const current = localAgenticFlowConfig?.costManagement || {};
                                  updateAgenticFlowConfig(['costManagement'], {
                                    ...current,
                                    budgetAlertThreshold: parseInt(text) || 80
                                  });
                                }}
                                keyboardType="numeric"
                                style={{
                                  backgroundColor: editorColors.background,
                                  borderRadius: 6,
                                  padding: 10,
                                  fontSize: 13,
                                  color: editorColors.text,
                                  borderWidth: 1,
                                  borderColor: editorColors.border
                                }}
                              />
                            </YStack>
                          </YStack>
                        )}
                      </Card>
                    </YStack>
                  )}
                </YStack>
              </YStack>
                  ) : editorMode === 'action' && activeTab === 'conditions' ? (
              /* Conditions Tab */
              <YStack gap={20}>
                {/* Info Banner */}
                <View style={{
                  backgroundColor: theme ? '#eff6ff' : '#1e3a8a',
                  borderRadius: 12,
                  padding: 16,
                  borderWidth: 1,
                  borderColor: theme ? '#93c5fd' : '#3b82f6'
                }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                    <MaterialCommunityIcons name="information" size={20} color={theme ? '#1e40af' : '#93c5fd'} />
                    <Text style={{ fontSize: 14, fontWeight: '600', color: theme ? '#1e40af' : '#93c5fd' }}>
                      Conditional Logic
                    </Text>
                  </View>
                  <Text style={{ fontSize: 12, color: theme ? '#3b82f6' : '#93c5fd', lineHeight: 18 }}>
                    Add conditions to control when this action should execute based on data from the schema.
                  </Text>
                </View>

                {/* Available Fields Info */}
                {availableFields.length > 0 ? (
                  <View style={{
                    padding: 12,
                    backgroundColor: theme ? '#dcfce7' : '#14532d',
                    borderRadius: 8
                  }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                      <MaterialCommunityIcons name="check-circle" size={14} color={theme ? '#16a34a' : '#86efac'} />
                      <Text style={{ fontSize: 12, fontWeight: '600', color: theme ? '#16a34a' : '#86efac' }}>
                        {availableFields.length} schema fields available
                      </Text>
                    </View>
                    <Text style={{ fontSize: 11, color: theme ? '#15803d' : '#4ade80' }}>
                      {availableFields.map(f => f.label).join(', ')}
                    </Text>
                  </View>
                ) : (
                  <View style={{
                    padding: 12,
                    backgroundColor: theme ? '#fef3c7' : '#78350f',
                    borderRadius: 8
                  }}>
                    <Text style={{ fontSize: 12, color: theme ? '#d97706' : '#fbbf24' }}>
                      ⚠️ No schema fields found. Conditions require schema fields to work with.
                    </Text>
                  </View>
                )}

                {/* Conditions List */}
                {conditions.length > 0 ? (
                  <YStack gap={12}>
                    {conditions.map((condition, index) => (
                      <View
                        key={condition.id}
                        style={{
                          backgroundColor: theme ? 'white' : '#1e293b',
                          padding: 14,
                          borderRadius: 12,
                          borderWidth: 1,
                          borderColor: editorColors.border
                        }}
                      >
                        {/* Condition Header */}
                        <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                          <View style={{
                            flexDirection: 'row',
                            alignItems: 'center',
                            backgroundColor: theme ? '#f3e8ff' : '#4c1d95',
                            paddingHorizontal: 8,
                            paddingVertical: 4,
                            borderRadius: 6
                          }}>
                            <MaterialCommunityIcons name="filter" size={12} color={theme ? '#7c3aed' : '#c4b5fd'} />
                            <Text style={{ fontSize: 11, fontWeight: '600', color: theme ? '#7c3aed' : '#c4b5fd', marginLeft: 4 }}>
                              Condition {index + 1}
                            </Text>
                          </View>
                          <Pressable
                            onPress={() => handleRemoveCondition(condition.id)}
                            style={{ padding: 4 }}
                          >
                            <MaterialIcons name="delete" size={18} color="#ef4444" />
                          </Pressable>
                        </View>

                        {/* Field Selector */}
                        <View style={{ marginBottom: 12 }}>
                          <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 6 }}>
                            IF field:
                          </Text>
                          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                            <XStack gap={6}>
                              {availableFields.map((field) => (
                                <Pressable
                                  key={field.value}
                                  onPress={() => handleUpdateCondition(condition.id, 'field', field.value)}
                                  style={{
                                    paddingHorizontal: 12,
                                    paddingVertical: 6,
                                    borderRadius: 6,
                                    backgroundColor: condition.field === field.value
                                      ? editorColors.accent
                                      : editorColors.cardBg,
                                    borderWidth: 1,
                                    borderColor: condition.field === field.value
                                      ? editorColors.accent
                                      : editorColors.border
                                  }}
                                >
                                  <Text style={{
                                    fontSize: 11,
                                    fontWeight: '600',
                                    color: condition.field === field.value
                                      ? 'white'
                                      : editorColors.text
                                  }}>
                                    {field.label}
                                  </Text>
                                </Pressable>
                              ))}
                            </XStack>
                          </ScrollView>
                        </View>

                        {/* Operator Selector */}
                        <View style={{ marginBottom: 12 }}>
                          <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 6 }}>
                            Operator:
                          </Text>
                          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                            <XStack gap={6}>
                              {availableOperators.map((op) => (
                                <Pressable
                                  key={op.value}
                                  onPress={() => handleUpdateCondition(condition.id, 'operator', op.value)}
                                  style={{
                                    paddingHorizontal: 10,
                                    paddingVertical: 6,
                                    borderRadius: 6,
                                    backgroundColor: condition.operator === op.value
                                      ? editorColors.accent
                                      : editorColors.cardBg,
                                    borderWidth: 1,
                                    borderColor: condition.operator === op.value
                                      ? editorColors.accent
                                      : editorColors.border
                                  }}
                                >
                                  <Text style={{
                                    fontSize: 10,
                                    fontWeight: '600',
                                    color: condition.operator === op.value
                                      ? 'white'
                                      : editorColors.text
                                  }}>
                                    {op.label}
                                  </Text>
                                </Pressable>
                              ))}
                            </XStack>
                          </ScrollView>
                        </View>

                        {/* Value Input */}
                        <View style={{ marginBottom: 12 }}>
                          <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 6 }}>
                            Value:
                          </Text>
                          <TextInput
                            value={condition.value}
                            onChangeText={(text) => handleUpdateCondition(condition.id, 'value', text)}
                            placeholder="Enter value to compare"
                            placeholderTextColor={editorColors.secondaryText}
                            style={{
                              backgroundColor: editorColors.cardBg,
                              borderRadius: 8,
                              padding: 10,
                              fontSize: 13,
                              color: editorColors.text,
                              borderWidth: 1,
                              borderColor: editorColors.border
                            }}
                          />
                        </View>

                        {/* Action Selector */}
                        <View>
                          <Text style={{ fontSize: 12, fontWeight: '600', color: editorColors.text, marginBottom: 6 }}>
                            THEN action:
                          </Text>
                          <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                            <XStack gap={6}>
                              {availableConditionActions.map((condAction) => (
                                <Pressable
                                  key={condAction.value}
                                  onPress={() => handleUpdateCondition(condition.id, 'action', condAction.value)}
                                  style={{
                                    paddingHorizontal: 12,
                                    paddingVertical: 6,
                                    borderRadius: 6,
                                    backgroundColor: condition.action === condAction.value
                                      ? editorColors.accent
                                      : editorColors.cardBg,
                                    borderWidth: 1,
                                    borderColor: condition.action === condAction.value
                                      ? editorColors.accent
                                      : editorColors.border
                                  }}
                                >
                                  <Text style={{
                                    fontSize: 11,
                                    fontWeight: '600',
                                    color: condition.action === condAction.value
                                      ? 'white'
                                      : editorColors.text
                                  }}>
                                    {condAction.label}
                                  </Text>
                                </Pressable>
                              ))}
                            </XStack>
                          </ScrollView>
                        </View>

                        {/* Preview */}
                        <View style={{
                          marginTop: 12,
                          padding: 10,
                          backgroundColor: theme ? '#f3e8ff' : '#4c1d95',
                          borderRadius: 6,
                          borderLeftWidth: 3,
                          borderLeftColor: editorColors.accent
                        }}>
                          <Text style={{ fontSize: 11, fontWeight: '600', color: theme ? '#6d28d9' : '#c4b5fd' }}>
                            IF {availableFields.find(f => f.value === condition.field)?.label || condition.field} {condition.operator} "{condition.value}" THEN {availableConditionActions.find(a => a.value === condition.action)?.label || condition.action}
                          </Text>
                        </View>
                      </View>
                    ))}
                  </YStack>
                ) : (
                  <View style={{
                    padding: 16,
                    backgroundColor: editorColors.cardBg,
                    borderRadius: 8,
                    alignItems: 'center'
                  }}>
                    <Text style={{ fontSize: 13, color: editorColors.secondaryText, textAlign: 'center' }}>
                      No conditions defined. Click "Add Condition" to create conditional logic.
                    </Text>
                  </View>
                )}

                {/* Add Condition Button */}
                <Pressable
                  style={{
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                    backgroundColor: editorColors.accent,
                    borderRadius: 8,
                    alignItems: 'center',
                    flexDirection: 'row',
                    justifyContent: 'center',
                    gap: 8,
                    marginTop: 8
                  }}
                  onPress={handleAddCondition}
                >
                  <MaterialIcons name="add" size={18} color="white" />
                  <Text style={{ color: 'white', fontWeight: '600', fontSize: 14 }}>
                    Add Condition
                  </Text>
                </Pressable>

                {/* Quick Guide */}
                <View style={{ marginTop: 16, padding: 12, backgroundColor: theme ? '#eff6ff' : '#1e3a8a', borderRadius: 8 }}>
                  <Text style={{ fontSize: 12, fontWeight: '600', color: theme ? '#1e40af' : '#93c5fd', marginBottom: 6 }}>
                    💡 How Conditions Work:
                  </Text>
                  <Text style={{ fontSize: 11, color: theme ? '#3b82f6' : '#93c5fd', lineHeight: 16 }}>
                    • Conditions are evaluated when the action is triggered{'\n'}
                    • All conditions must be met (AND logic){'\n'}
                    • If conditions fail, the specified action (skip/warn/block) is taken{'\n'}
                    • Use conditions to add smart validation and control flow
                  </Text>
                </View>
              </YStack>
            ) : (
              /* Configuration Tab */
              <YStack gap={20}>
                {/* Action Selection Dropdown */}
                <YStack gap={8}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                    Select Action
                  </Text>
                  <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                    <XStack gap={8}>
                      {actionList.map(action => (
                        <Pressable
                          key={action.key}
                          onPress={() => setSelectedActionKey(action.key)}
                          style={{
                            paddingHorizontal: 16,
                            paddingVertical: 10,
                            borderRadius: 8,
                            backgroundColor: selectedActionKey === action.key ? editorColors.accent : editorColors.cardBg,
                            borderWidth: 1,
                            borderColor: selectedActionKey === action.key ? editorColors.accent : editorColors.border,
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 8
                          }}
                        >
                          <MaterialCommunityIcons
                            name={action.icon as any}
                            size={20}
                            color={selectedActionKey === action.key ? 'white' : editorColors.text}
                          />
                          <Text style={{
                            fontSize: 13,
                            fontWeight: '600',
                            color: selectedActionKey === action.key ? 'white' : editorColors.text,
                            textTransform: 'capitalize'
                          }}>
                            {action.title}
                          </Text>
                        </Pressable>
                      ))}
                    </XStack>
                  </ScrollView>
                </YStack>

                {/* Action Name */}
                <YStack gap={8}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                    Action Name
                  </Text>
                  <TextInput
                    value={formData.name}
                    onChangeText={(text) => setFormData(prev => ({ ...prev, name: text }))}
                    placeholder={`Enter ${selectedActionType.toLowerCase()} action name`}
                    placeholderTextColor={editorColors.secondaryText}
                    style={{
                      backgroundColor: editorColors.cardBg,
                      borderRadius: 8,
                      padding: 12,
                      fontSize: 14,
                      color: editorColors.text,
                      borderWidth: 1,
                      borderColor: editorColors.border
                    }}
                  />
                </YStack>

                {/* Description */}
                <YStack gap={8}>
                  <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                    Description
                  </Text>
                  <TextInput
                    value={formData.description}
                    onChangeText={(text) => setFormData(prev => ({ ...prev, description: text }))}
                    placeholder="Describe what this action does"
                    placeholderTextColor={editorColors.secondaryText}
                    multiline
                    numberOfLines={3}
                    style={{
                      backgroundColor: editorColors.cardBg,
                      borderRadius: 8,
                      padding: 12,
                      fontSize: 14,
                      color: editorColors.text,
                      borderWidth: 1,
                      borderColor: editorColors.border,
                      minHeight: 80,
                      textAlignVertical: 'top'
                    }}
                  />
                </YStack>

                          {/* Workflow Assignment Section */}
                          <YStack gap={12} marginBottom={16}>
                            <Text style={{ fontSize: 14, fontWeight: '600', color: editorColors.text }}>
                              Workflow Assignment
                            </Text>
                            {currentWorkflowId ? (
                              <View style={{
                                backgroundColor: editorColors.cardBg,
                                borderRadius: 8,
                                padding: 12,
                                borderWidth: 1,
                                borderColor: editorColors.accent,
                                flexDirection: 'row',
                                alignItems: 'center',
                                justifyContent: 'space-between'
                              }}>
                                <XStack alignItems="center" gap={8}>
                                  <MaterialIcons name="account-tree" size={20} color={editorColors.accent} />
                                  <Text style={{ fontSize: 13, color: editorColors.text }}>
                                    Workflow assigned
                                  </Text>
                                </XStack>
                                <Pressable
                                  onPress={() => {
                                    // Switch to workflow mode
                                    handleModeSwitch();
                                  }}
                                  style={{
                                    paddingHorizontal: 12,
                                    paddingVertical: 6,
                                    backgroundColor: editorColors.accent,
                                    borderRadius: 6
                                  }}
                                >
                                  <Text style={{ fontSize: 12, fontWeight: '600', color: 'white' }}>
                                    Edit Workflow
                                  </Text>
                                </Pressable>
                              </View>
                            ) : (
                              <View style={{
                                backgroundColor: editorColors.cardBg,
                                borderRadius: 8,
                                padding: 12,
                                borderWidth: 1,
                                borderColor: editorColors.border
                              }}>
                                <Text style={{ fontSize: 12, color: editorColors.secondaryText, marginBottom: 8 }}>
                                  No workflow assigned to this action
                                </Text>
                                <Button
                                  backgroundColor={editorColors.accent}
                                  borderRadius={8}
                                  paddingHorizontal="$4"
                                  paddingVertical="$3"
                                  onPress={() => {
                                    // Switch to workflow mode to create workflow
                                    handleModeSwitch();
                                  }}
                                >
                                  <SizableText color="white" fontWeight="600" size="$3">
                                    Create Workflow
                                  </SizableText>
                                </Button>
                              </View>
                            )}
                          </YStack>

                {/* Dynamic Action Configuration Form */}
                <View style={{
                  backgroundColor: editorColors.cardBg,
                  borderRadius: 12,
                  paddingVertical: 16,

                  borderWidth: 1,
                  borderColor: editorColors.border,
                  minHeight: 200
                }}>
                  <Text style={{ left: 20, fontSize: 14, fontWeight: '600', color: editorColors.text, marginBottom: 12 }}>
                    {actionList.find(a => a.key === selectedActionKey)?.title} Configuration
                  </Text>
                  <View style={{ left: -10, top: -50 }}>
                    {(() => {
                      const SelectedComponent = actionList.find(a => a.key === selectedActionKey)?.component;
                      if (SelectedComponent) {
                        return React.createElement(SelectedComponent, {
                          ref: formRef,
                          index: actions.length,
                          initialVals: {}
                        } as any);
                      }
                      return (
                        <Text style={{ fontSize: 13, color: editorColors.secondaryText, textAlign: 'center', padding: 20 }}>
                          No configuration component available for this action.
                        </Text>
                      );
                    })()}
                  </View>
                </View>
              </YStack>
            )}
          </ScrollView>
        </YStack>

        {/* Footer Section */}
        <YStack
          padding="$5"
          paddingTop="$4"
          borderTopWidth={1}
          borderTopColor={editorColors.border}
          backgroundColor={editorColors.surface}
        >
          <XStack gap="$3" justifyContent="space-between">
            <Button
              backgroundColor={editorColors.cardBg}
              borderColor={editorColors.border}
              borderWidth={1}
              borderRadius={12}
              paddingHorizontal="$5"
              paddingVertical="$3"
              minHeight={48}
              pressStyle={{
                backgroundColor: editorColors.surface,
                scale: 0.98,
              }}
              aria-label="Cancel"
              onPress={onClose}
            >
              <SizableText
                color={editorColors.secondaryText}
                fontWeight="600"
                size="$4"
              >
                Cancel
              </SizableText>
            </Button>

            <XStack gap="$3">
              {/* Skip button - only show on conditions tab */}
              {activeTab === 'conditions' && (
                <Button
                  backgroundColor={theme ? '#f59e0b' : '#d97706'}
                  borderRadius={12}
                  paddingHorizontal="$5"
                  paddingVertical="$3"
                  minHeight={48}
                  pressStyle={{
                    backgroundColor: theme ? '#d97706' : '#f59e0b',
                    scale: 0.98,
                  }}
                  aria-label="Skip"
                  onPress={onSave}
                >
                  <SizableText
                    color="#ffffff"
                    fontWeight="600"
                    size="$4"
                  >
                    Skip & Save
                  </SizableText>
                </Button>
              )}

              <Button
                backgroundColor={editorColors.accent}
                borderRadius={12}
                paddingHorizontal="$5"
                paddingVertical="$3"
                minHeight={48}
                pressStyle={{
                  backgroundColor: theme ? '#0284c7' : '#38bdf8',
                  scale: 0.98,
                }}
                shadowColor="#000"
                shadowOffset={{ width: 0, height: 2 }}
                shadowOpacity={0.1}
                shadowRadius={4}
                elevation={2}
                aria-label="Save"
                onPress={() => {
                  if (activeTab === 'workflowNodes') {
                    // Create workflow node
                    console.log('🔘 Add Node button pressed', { selectedNodeType, selectedNodeCategory, onWorkflowNodeAdd: !!onWorkflowNodeAdd });
                    if (selectedNodeType && selectedNodeCategory) {
                      const nodeTypeDef = getNodeType(selectedNodeType);
                      console.log('📦 Node type definition:', nodeTypeDef);
                      if (nodeTypeDef && onWorkflowNodeAdd) {
                        const newNode = {
                          type: selectedNodeType,
                          name: nodeTypeDef.name,
                          category: selectedNodeCategory,
                          position: {
                            x: Math.random() * 300 + 100,
                            y: Math.random() * 200 + 100
                          },
                          config: {}
                        };
                        console.log('✅ Calling onWorkflowNodeAdd with:', newNode);
                        onWorkflowNodeAdd(newNode);
                        // Reset selection for next node (but keep modal open)
                        setSelectedNodeCategory(null);
                        setSelectedNodeType(null);
                      } else {
                        console.error('❌ Missing nodeTypeDef or onWorkflowNodeAdd', { nodeTypeDef: !!nodeTypeDef, onWorkflowNodeAdd: !!onWorkflowNodeAdd });
                        Alert.alert('Error', 'Unable to add node. Please try again.');
                      }
                    } else {
                      console.warn('⚠️ Missing selection', { selectedNodeType, selectedNodeCategory });
                      Alert.alert('Selection Required', 'Please select a node category and type');
                    }
                  } else if (activeTab === 'type') {
                    setActiveTab('config');
                  } else if (activeTab === 'config') {
                    setActiveTab('conditions');
                  } else if (activeTab === 'conditions') {
                    setActiveTab('agentic');
                  } else if (editorMode === 'workflow') {
                    // Save workflow
                    onWorkflowSave?.();
                  } else {
                    // Save action
                    onSave();
                  }
                }}
              >
                <SizableText
                  color="#ffffff"
                  fontWeight="700"
                  size="$4"
                >
                  {activeTab === 'workflowNodes' ? 'Add Node' :
                    activeTab === 'type' ? 'Next: Configuration' :
                      activeTab === 'config' ? 'Next: Conditions' :
                        activeTab === 'conditions' ? 'Next: Agentic Flow' :
                          activeTab === 'agentic' ? 'Save Action' :
                            activeTab === 'workflow' ? 'Save Workflow' :
                              'Save Action'}
                </SizableText>
              </Button>
            </XStack>
          </XStack>
        </YStack>
      </YStack>
    </MotiView>
  );
};

export default ActionEditorSlidePanel;

